import { inject, Injectable } from '@angular/core';
import { NBasketFreeShipping } from '@df/basket/free-shipping/basket-free-delivery-notice.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { IResponseRemoveFromBasket } from '@df/session/api/basket/basket.service';
import keys from 'lodash-es/keys';
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';
import uniq from 'lodash-es/uniq';
import values from 'lodash-es/values';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { GiftboxBasketItem } from '../../../basket/giftbox/giftbox-basket-item';
import { GiftboxService } from '../../../basket/giftbox/giftbox.service';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketData<
            TItemId extends number | string = number,
            TProductId extends number | string = number,
            TQuoteId extends number | string = number
        > {
            prevent_payment_method?: string[];
            freeshipping?: Record<string, boolean>;
            freeshippingminamounts?: NBasketFreeShipping.IFreeshippingminamount[];
            contains_furniture?: boolean;
            not_furniture_only?: boolean;
            custom_rules?: number[];
        }
    }
}

export interface IBasketDataFreeShippingMinAmount {
    value: number;
    name: string;
}

@Injectable({
    providedIn: 'root'
})
export class BasketDecorator extends Basket {
    readonly displayedDataUpdateSubject = new Subject<void>();

    /**
     * items that are displayed in basket, we keep displaying deleted items for a while to show remove success message.
     */
    displayedBasketItems: BasketItem[] = [];

    /**
     * same as above for giftbox items
     */
    displayedGiftboxBasketItems: GiftboxBasketItem[] = [];

    removeCouponStatus = new Status();

    private _loadGiftboxItems = false;

    protected giftboxService = inject(GiftboxService);
    protected _app = inject(App);
    protected readonly disposableTimeoutService = injectTimeoutService();

    /**
     * Register observer
     * Observer function are called on displayed data update
     * @param  {() => void} callback
     */
    subscribeDisplayedData(...args): Subscription {
        return this.displayedDataUpdateSubject.subscribe(...args);
    }

    async preloadData() {
        const productIds = this.getProductIdsToLoad();

        await super.loadItemsDependecies(productIds);

        this._loadGiftboxItems = true;

        this.onDataUpdate(this.createItems(this.data.items));

        const getBoxItems = await this.giftboxService.getBoxItems();

        // Delay while item is removed for message to show.
        if (this.items?.length < this.displayedBasketItems?.length || getBoxItems?.length < this.displayedGiftboxBasketItems?.length) {
            this.disposableTimeoutService.setTimeout(() => this.displayedDataUpdate(), 3000);
        } else {
            this.displayedDataUpdate();
        }
    }

    override async onDataUpdate(items: BasketItem[]): Promise<void> {
        super.onDataUpdate(items);

        const ids: number[] = [];

        this.items.map(item => {
            if (item.product?.parent?.id) {
                ids.push(item.product?.parent?.id);
            }
        });

        await this._app.resources.product.loadAllIds(uniq(ids), 3, { safe: true });

        this.displayedDataUpdate();
    }

    getFreeShippingMinAmounts(allowed: Checkout.IShippingMethod[]): IBasketDataFreeShippingMinAmount[] {
        if (this.data?.freeshipping && this.data?.freeshippingminamounts && allowed?.length) {
            const freeShippingAmounts = values(
                pick(
                    pickBy(
                        this.data.freeshippingminamounts,
                        (item, key) =>
                            !!allowed.find(i => {
                                if (i.code) {
                                    const carrier = i.code.split('_')[0];
                                    return carrier === key;
                                }
                                return false;
                            })
                    ),
                    keys(this.data.freeshipping)
                )
            );
            return freeShippingAmounts;
        }
        return [];
    }

    async displayedDataUpdate() {
        this.displayedBasketItems = this.items;

        if (this._loadGiftboxItems || this.displayedGiftboxBasketItems?.length) {
            this.displayedGiftboxBasketItems = await this.giftboxService.getBoxItems();
        }

        this.displayedDataUpdateSubject.next();
    }

    override removeCoupon() {
        return super.removeCoupon(this.removeCouponStatus, {
            coupon: this.customer.basket.data.coupon,
            // we use fast checkout on entire website so checkout must always be true.
            checkout: true
        });
    }

    removeOOSProducts(status: Status): void {
        status.busy = true;

        const promises: Promise<IResponseRemoveFromBasket>[] = [];

        this.items.map(item => {
            if (!item.isInStock()) {
                promises.push(item.remove(undefined, undefined, undefined, false));
            }
        });

        Promise.all(promises)
            .catch(err => {
                status.error = err;
            })
            .finally(() => {
                status.success = 'All out of stock products were removed from the basket';
            });
    }

    get isKlarnaSlice(): boolean {
        return !this.data.prevent_payment_method?.filter(method => method.match(/klarna_pay_over_time/i)).length;
    }

    get hasOOSProducts(): boolean {
        let hasProductsOOS = false;

        if (this.items.length) {
            hasProductsOOS = !!this.items.find(item => !item.isInStock());
        }

        return hasProductsOOS;
    }

    get qtyWithoutOOS(): number {
        return this.items.reduce((acc, item) => acc + (item.isInStock() ? item.qty : 0), 0);
    }
}
